import classNames from "classnames";
import Percolator from "../../helpers/Percolator";
import Sb from "../abstract/StatefulBehavior";
import ToggleSubmenu from "./ToggleSubmenu";
import debounce from "lodash/debounce";

export default class ControlSubmenus extends Sb {
  constructor(el, props, refs) {
    super();

    this.state = {
      open: false,
      submenuOpen: false,
      searchOpen: false,
    };

    this.el = el;
    this.refs = refs;
    this.initElClass = el.className;

    this.instantiateSubmenus();
    this.update();
    this.bindToggle();
    this.bindResize();
  }

  get focusable() {
    const { searchOpen } = this.state;

    if (searchOpen) {
      return [this.refs.searchToggle].concat(
        Array.from(
          this.refs.searchBar.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])' // eslint-disable-line max-len
          )
        )
      );
    }

    return [this.refs.menuToggle].concat(
      Array.from(
        this.refs.menuBar.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])' // eslint-disable-line max-len
        )
      )
    );
  }

  get firstFocusable() {
    return this.focusable[0];
  }

  get lastFocusable() {
    return this.focusable[this.focusable.length - 1];
  }

  instantiateSubmenus() {
    const percolator = new Percolator();

    // Instantiate (and track) the submenus
    // with a callback that runs the "close"
    // function on all but the active hover
    this.submenus = this.refs.submenu.map((submenu, index) => {
      return percolator.loadBehavior(submenu, ToggleSubmenu, {
        id: index,
        onOpen: this.handleOpen,
        onActiveClose: this.handleActiveClose,
      });
    });
  }

  update = () => {
    const { menuToggle, menuBar, searchToggle, searchBar } = this.refs;
    const { open, searchOpen, submenuOpen } = this.state;

    // Set classes
    this.el.className = classNames(this.initElClass, {
      "has-nav-untoggled": !open && !submenuOpen,
      "has-nav-toggled": open,
      "has-subnav-toggled": submenuOpen,
      "has-search-toggled": searchOpen,
    });

    if (open) {
      menuBar.classList.add(`m-mobilenav--is-visible`);
    } else {
      menuBar.classList.remove(`m-mobilenav--is-visible`);
    }

    // Set attributres
    menuToggle.setAttribute("aria-expanded", open);
    searchToggle.setAttribute("aria-expanded", searchOpen);
    searchBar.setAttribute("aria-hidden", !searchOpen);
    menuBar.setAttribute("aria-hidden", !open);

    // Toggle menus
    this.toggleFocus();

    // Update scroll lock
    this.updateScrollLock();
  };

  bindToggle() {
    this.refs.menuToggle.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      const { open } = this.state;
      this.setState({ open: !open, searchOpen: false });
    });

    this.refs.searchToggle.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      const { open, searchOpen } = this.state;
      this.setState({ open: false, searchOpen: !searchOpen });
    });
  }

  bindResize() {
    // Set to closed state if at wider viewport width
    const debouncedInnerWidth = debounce(() => {
      if (!this.state.open && !this.state.searchOpen) return null;

      const isMobileWidth = window.innerWidth < 850;
      !isMobileWidth && this.setState({ open: false, searchOpen: false });
    }, 200);

    window.addEventListener("resize", debouncedInnerWidth);
  }

  handleOpen = (active) => {
    this.setState({ submenuOpen: true });
    this.closeOtherSubmenus(active);
  };

  handleActiveClose = () => {
    this.setState({ submenuOpen: false });
    this.closeOtherSubmenus();
  };

  toggleFocus = () => {
    const { open, searchOpen, submenuOpen } = this.state;
    if (open || searchOpen || submenuOpen) {
      window.addEventListener("keyup", this.handleKeyUp);
      this.trapFocus();
      if (searchOpen) {
        this.refs.searchInput.focus();
      }
    } else {
      window.removeEventListener("keyup", this.handleKeyUp);
      this.closeOtherSubmenus();
    }
  };

  closeOtherSubmenus = (active) => {
    this.submenus.forEach((menu, index) => {
      if (index !== active) {
        menu.closeMenu();
      }
    });
  };

  handleKeyUp = (event) => {
    event.preventDefault();
    event.stopPropagation();

    this.handleEscape(event);
    this.handleTab(event);
  };

  handleEscape({ key, keyCode, which }) {
    if (key === "Escape" || key === "Esc" || keyCode === 27 || which === 27) {
      this.setState({ open: false, searchOpen: false });
      // Only auto-focus on the menu toggle if escape is pressed.
      this.refs.menuToggle.focus();
    }
  }

  handleTab({ key, keyCode, shiftKey }) {
    const isTabPressed = key === "Tab" || keyCode === 9;
    if (!isTabPressed) return;

    if (!this.focusable || !this.focusable.length) return false;

    const focusedItem = document.activeElement;
    const focusableIndex = this.focusable.indexOf(focusedItem);

    if (focusableIndex >= 0) return;

    shiftKey ? this.lastFocusable.focus() : this.firstFocusable.focus();
  }

  trapFocus() {
    if (!this.state.open || !this.focusable || !this.focusable.length) return;
    this.focusable[0].focus();
  }

  updateScrollLock() {
    const { classList } = document.body;
    const classNames = ["has-scroll-lock", "drawer-is-open"];

    classNames.forEach((className) => {
      if (this.state.open) {
        !classList.contains(className) && classList.add(className);
      } else {
        classList.contains(className) && classList.remove(className);
      }
    });
  }
}
